/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable react/jsx-props-no-spreading */
import { useCallback, useId, useMemo, useState } from "react";

import {
  AutocompleteHookForm,
  MultiAutocompleteHookForm,
  SearchSmIcon,
} from "components";
import { usePermission } from "hooks";
import uniqBy from "lodash/uniqBy";
import { renderCustomOption } from "pages/utils";
import { useQuery } from "react-query";
import { checkMainLan, getLabelPayrollCycle, getOptionsSelect } from "utils";

import { MappingPermission } from "./constants";
import useSearchAutoComplete from "./hook";
import { ModelName } from "./types";
import { getServices } from "./utils";

interface Props {
  label?: string;
  placeholder?: string;
  name: string;
  required?: boolean;
  disabled?: boolean;
  model: ModelName;
  keyValue?: string;
  keyLabel?: string;
  keySubLabel?: string;
  exclude?: string[];
  depField?: string | number | (string | number)[];
  useDepField?: boolean;
  multiple?: boolean;
  limitTags?: number;
  withSearchIcon?: boolean;
}

const defaultPage = 1;
const defaultLimit = 50;

type ShortItem = {
  id: number | string;
  code: string;
  name: string;
  nativeName?: string;
};

const AutocompleteHookFormAsync = (props: Props) => {
  const {
    label,
    placeholder,
    name,
    required,
    model = "country",
    disabled,
    keyValue = "id",
    keyLabel,
    keySubLabel,
    exclude = [],
    depField,
    useDepField,
    multiple = false,
    limitTags,
    withSearchIcon = false,
  } = props;
  const queryKey = useId();
  const { canAccess } = usePermission();
  const [page, setPage] = useState(defaultPage);
  const [hasMore, setHasMore] = useState(true);
  const [data, setData] = useState<ShortItem[]>([]);
  const searchAutocomplete = useSearchAutoComplete<string>(undefined, () => {
    setPage(defaultPage);
    setHasMore(true);
    setData([]);
  });

  const enableQuery = useMemo(() => {
    if (!canAccess(MappingPermission[model])) return false;
    if (useDepField) {
      return searchAutocomplete.isFocus && !!depField;
    }
    return searchAutocomplete.isFocus;
  }, [canAccess, depField, model, searchAutocomplete.isFocus, useDepField]);

  const { isFetching: isLoading } = useQuery({
    queryKey: [
      queryKey,
      searchAutocomplete.keyword,
      depField,
      page,
      searchAutocomplete?.forceRetry,
    ],
    enabled: enableQuery,
    queryFn: () =>
      getServices(model, searchAutocomplete.keyword, depField as never, page),
    onSuccess(res: any) {
      if (page === defaultPage) {
        setData(res?.data?.items as ShortItem[]);
      } else {
        setData((prevData) =>
          uniqBy([...prevData, ...(res?.data?.items || [])], keyValue)
        );
      }
      if (res?.data?.items?.length === defaultLimit) {
        setHasMore(true);
      } else {
        setHasMore(false);
      }
    },
  });

  const keyLabelLan = useMemo(() => {
    if (keyLabel) return keyLabel;
    if (
      !keyLabel &&
      [
        "country",
        "province",
        "district",
        "ward",
        "earning",
        "deduction",
        "allowance",
        "workLevel",
        "leavePolicy",
        "compensation",
        "payroll-template",
        "contractType",
        "workShift",
        "leaveType",
        "bank",
        "approvalWorkflow",
        "payroll-template-pic",
        "benefitsInKind",
        "productivity-template",
        "productivity-template-me",
        "productivity-workShift",
      ]?.includes(model)
    ) {
      return checkMainLan() ? "nativeName" : "name";
    }
    return "name";
  }, [keyLabel, model]);

  const subLabel = useMemo(() => {
    if (keySubLabel) return keySubLabel;
    if (["department", "title", "employee"]?.includes(model)) {
      return "code";
    }
    return "";
  }, [keySubLabel, model]);

  const options = useMemo(() => {
    let newData = data;
    if (model === "payrollCycle") {
      newData = data.map(
        (item: any) =>
          ({
            ...item,
            name: getLabelPayrollCycle(item),
          } as ShortItem)
      );
    }
    return [
      ...getOptionsSelect<string>({
        data: newData || [],
        keyLabel: keyLabelLan,
        keyValue,
      }).filter((item) => !exclude?.includes(String(item.value))),
    ];
  }, [data, exclude, keyLabelLan, keyValue, model]);

  const handleLoadMore = useCallback(() => {
    if (hasMore) {
      setPage((prevPage) => prevPage + 1);
    }
  }, [hasMore]);

  if (multiple) {
    return (
      <MultiAutocompleteHookForm
        name={name}
        label={label}
        placeholder={placeholder}
        options={options}
        onInputChange={searchAutocomplete.setKeyword}
        onFocus={searchAutocomplete.onFocus}
        onBlur={searchAutocomplete.onBlur}
        required={required}
        disabled={disabled}
        loading={isLoading}
        onLoadMore={handleLoadMore}
        prefix={withSearchIcon ? <SearchSmIcon fontSize="small" /> : undefined}
        {...(limitTags && { limitTags })}
        {...(subLabel && {
          renderOption: (_, option) =>
            renderCustomOption(
              option.original,
              String(keyLabelLan || ""),
              subLabel
            ),
        })}
        disabledLocalSearch
      />
    );
  }

  return (
    <AutocompleteHookForm
      name={name}
      label={label}
      placeholder={placeholder}
      options={options}
      onInputChange={searchAutocomplete.setKeyword}
      onFocus={searchAutocomplete.onFocus}
      required={required}
      disabled={disabled}
      loading={isLoading}
      onLoadMore={handleLoadMore}
      prefix={withSearchIcon ? <SearchSmIcon fontSize="small" /> : undefined}
      {...(limitTags && { limitTags })}
      {...(subLabel && {
        renderOption: (_, option) =>
          renderCustomOption(
            option.original,
            String(keyLabelLan || ""),
            subLabel
          ),
      })}
      disabledLocalSearch
    />
  );
};

export default AutocompleteHookFormAsync;
